import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import {
  fetchESPRealtimeData,
  fetchGasLiftRealtimeData,
  fetchRodLiftRealtimeData,
  selectESPData,
  selectGasLiftData,
  selectRodLiftData,
  clearESPData,
  clearGasLiftData,
  clearRodLiftData,
} from './RealtimeInsightsSlice';
import HeaderElements from '../navigation/components/HeaderElements';
import UIButtonToolbar from '../navigation/components/UIButtonToolbar';
import '../recommendations/Recommendations.scss'; // Reuse recommendations styles
import { useSelector } from 'react-redux';
import './RealtimeInsights.scss';
import RealTimeWidget from './components/RealTimeWidget';

interface RealtimeInsightsProps {
  mop?: string;
  valueFromStatus?: number;
  status?: string;
}

export interface TabContent {
  id: number;
  name: string;
  active: boolean;
  count?: number;
}

export const RealtimeInsights: React.FC<RealtimeInsightsProps> = ({
  mop: propMop,
  valueFromStatus: propValueFromStatus,
  status: propStatus,
}) => {
  const location = useLocation();

  // Get data from props or location state (for navigation)
  const mop = propMop || location.state?.mop || 'Unknown';
  const valueFromStatus =
    propValueFromStatus !== undefined ? propValueFromStatus : location.state?.valueFromStatus || 0;
  const status = propStatus || location.state?.status || '';
  console.log(valueFromStatus);
  // Map MOP to tab - determine initial tab based on MOP
  const getInitialTab = (mopValue: string): string => {
    const upperMop = mopValue.toUpperCase();
    if (upperMop.includes('ESP') || upperMop === 'ESP') return 'esp';
    if (upperMop.includes('GAS') || upperMop === 'GL' || upperMop === 'GLA') return 'gl';
    if (upperMop.includes('ROD') || upperMop === 'SRP') return 'rl';
    return 'esp'; // Default to ESP
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialTab(mop));
  const [scrollY, setScrollY] = useState(0);

  // Individual state values for tab counts
  const [espCount, setEspCount] = useState<number>(0);
  const [gasLiftCount, setGasLiftCount] = useState<number>(0);
  const [rodLiftCount, setRodLiftCount] = useState<number>(0);

  // Redux hooks and state
  const dispatch = useAppDispatch();
  const realtimeState = useAppSelector((state) => state.realtimeInsights);
  const wellFilters = useAppSelector((state) => state.WellFiltersSlice);
  const espData = useSelector(selectESPData);
  const gasLiftData = useSelector(selectGasLiftData);
  const rodLiftData = useSelector(selectRodLiftData);
  // Helper function to get current data based on active tab
  const getCurrentData = () => {
    switch (activeTab) {
      case 'esp':
        return espData || [];
      case 'gl':
        return gasLiftData || [];
      case 'rl':
        return rodLiftData || [];
      default:
        return [];
    }
  };

  // Export functionality - simple handler for RealTimeWidget
  const handleExport = () => {
    try {
      const currentData = getCurrentData();
      if (!currentData?.length) {
        console.warn('No data available for export');
        return;
      }

      const labelMapSentenceCase: Record<string, string> = {
        alarms: 'Alarms',
        'Freq (Hz)': 'Freq (hz)',
        ControlMode: 'Control mode',
        'Pump Size': 'Pump size',
        'Pump Intake Pressure': 'Pump intake pressure',
        'Flowing Pressure Channel': 'Flowing pressure channel',
        'PDP(psig)': 'PDP (psig)',
        'Gas Inj. Rate': 'Gas inj. rate',
        'Tubing Temp': 'Tubing temp',
        'Tubing Temperature': 'Tubing temperature'
      };

      const formatLabel = (key: string): string => {
        return labelMapSentenceCase[key] || key;
      };

      // Import XLSX dynamically to avoid bundle size issues
      import('xlsx').then((XLSX) => {
        // Create worksheet data
        const worksheetData: string[][] = [];

        // Add headers based on first row keys
        if (currentData[0]) {
          const headers = ['Well name', 'Status', 'Alarms'];

          // Add wellDetails headers
          const wellDetails = currentData[0].wellDetails;
          if (wellDetails) {
            const wellDetailKeys = Object.keys(wellDetails).map(formatLabel);
            headers.push(...wellDetailKeys);
          }

          worksheetData.push(headers);

          // Add data rows
          currentData.forEach((item) => {
            const rowData = [
              item.wellName || '',
              item.status || '',
              item.alarms !== null && item.alarms !== undefined ? String(item.alarms) : '',
            ];

            // Add wellDetails values
            if (item.wellDetails) {
              rowData.push(...Object.values(item.wellDetails).map((val) => String(val || '')));
            }

            worksheetData.push(rowData);
          });
        }

        // Create worksheet and workbook
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, getTabDisplayName());

        // Generate file and download
        const fileName = `${getTabDisplayName()}_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
      });
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Helper function to get tab display name for export
  const getTabDisplayName = () => {
    switch (activeTab) {
      case 'esp':
        return 'ESP well status';
      case 'gl':
        return 'Gas lift well status';
      case 'rl':
        return 'Rod lift well status';
      default:
        return 'Well status';
    }
  };

  // Handle scroll for sticky header (similar to Recommendations)
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setScrollY(scrollTop);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Helper function to extract filter data for API calls
  const getFilterData = () => {
    const businessUnits =
      wellFilters.selectedFilters
        ?.filter((filter) => filter.section === 'Business unit')
        .map((filter) => filter.value)
        .join(',') || '';

    const fields =
      wellFilters.selectedFilters
        ?.filter((filter) => filter.section === 'Field')
        .map((filter) => filter.value)
        .join(',') || '';

    const groups =
      wellFilters.selectedFilters
        ?.filter((filter) => filter.section === 'Groups')
        .map((filter) => filter.value)
        .join(',') || '';

    const statuses = status || '';

    return {
      businessUnits,
      fields,
      groups,
      statuses,
    };
  };

  // Function to make API call based on active tab
  const fetchDataForTab = (tab: string) => {
    const filterData = getFilterData();

    switch (tab) {
      case 'esp':
        dispatch(fetchESPRealtimeData(filterData));
        break;
      case 'gl':
        dispatch(fetchGasLiftRealtimeData(filterData));
        break;
      case 'rl':
        dispatch(fetchRodLiftRealtimeData(filterData));
        break;
      default:
        break;
    }
  };

  // Effect to fetch data when component mounts or filters change
  useEffect(() => {
    fetchDataForTab(activeTab);
  }, [activeTab, wellFilters.selectedFilters, status]);

  // Effect to initialize count values from props based on initial tab
  useEffect(() => {
    const initialTab = getInitialTab(mop);
    if (initialTab === 'esp' && valueFromStatus > 0) {
      setEspCount(valueFromStatus);
    } else if (initialTab === 'gl' && valueFromStatus > 0) {
      setGasLiftCount(valueFromStatus);
    } else if (initialTab === 'rl' && valueFromStatus > 0) {
      setRodLiftCount(valueFromStatus);
    }
  }, [mop, valueFromStatus]);

  // Effect to update count when API data is received
  useEffect(() => {
    if (realtimeState.espData && Array.isArray(realtimeState.espData)) {
      setEspCount(realtimeState.espData.length);
    }
  }, [realtimeState.espData]);

  useEffect(() => {
    if (realtimeState.gasLiftData && Array.isArray(realtimeState.gasLiftData)) {
      setGasLiftCount(realtimeState.gasLiftData.length);
    }
  }, [realtimeState.gasLiftData]);

  useEffect(() => {
    if (realtimeState.rodLiftData && Array.isArray(realtimeState.rodLiftData)) {
      setRodLiftCount(realtimeState.rodLiftData.length);
    }
  }, [realtimeState.rodLiftData]);

  // Helper function to get count for each tab type - only show count for active tab
  const getTabCount = (tabType: string) => {
    // Only return count if this tab is the active tab
    if (tabType !== activeTab) {
      return undefined; // Don't show count for inactive tabs
    }

    switch (tabType) {
      case 'esp':
        return espCount;
      case 'gl':
        return gasLiftCount;
      case 'rl':
        return rodLiftCount;
      default:
        return undefined;
    }
  };

  // Tab configuration
  const tabButtons: TabContent[] = useMemo(
    () => [
      {
        id: 0,
        name: 'ESP well status',
        active: activeTab === 'esp',
        count: getTabCount('esp'),
      },
      {
        id: 1,
        name: 'Gas lift well status',
        active: activeTab === 'gl',
        count: getTabCount('gl'),
      },
      {
        id: 2,
        name: 'Rod lift well status',
        active: activeTab === 'rl',
        count: getTabCount('rl'),
      },
    ],
    [activeTab, espCount, gasLiftCount, rodLiftCount],
  );

  // Function to clear data and count for tabs other than the active one
  const clearInactiveTabsData = (currentActiveTab: string) => {
    if (currentActiveTab !== 'esp') {
      setEspCount(0);
      dispatch(clearESPData());
    }
    if (currentActiveTab !== 'gl') {
      setGasLiftCount(0);
      dispatch(clearGasLiftData());
    }
    if (currentActiveTab !== 'rl') {
      setRodLiftCount(0);
      dispatch(clearRodLiftData());
    }
  };

  const handleTabChange = (tabId: number) => {
    const tab = tabButtons.find((button) => button.id === tabId);
    if (tab) {
      let newActiveTab = '';

      if (tab.name === 'ESP well status') {
        newActiveTab = 'esp';
      } else if (tab.name === 'Gas lift well status') {
        newActiveTab = 'gl';
      } else if (tab.name === 'Rod lift well status') {
        newActiveTab = 'rl';
      }

      // Clear data from inactive tabs
      clearInactiveTabsData(newActiveTab);

      // Set the new active tab
      setActiveTab(newActiveTab);
    }
  };

  return (
    <div className='flex h-full flex-column chart-data'>
      {/* Header similar to Recommendations */}
      <div className={`summary-header-container ${scrollY > 0 ? 'fix-nav-bar' : ''}`} style={{ paddingLeft: '34px' }}>
        <div className='group-status-top-row'>
          <div className='group-status-group-name'>Realtime</div>
          <div className='custom-views-header-elements'>
            <HeaderElements showWellsTab={true} showFilterIcon={false} />
          </div>
        </div>
      </div>
      <div className='asset-ui-buttons-container recommendations-ui-buttons'>
        <UIButtonToolbar uiButtons={tabButtons} buttonClicked={handleTabChange} />
      </div>
      {/* Tab Content */}
      <div className='flex h-full flex-column'>
        {/* ESP Well Status Tab */}
        {activeTab === 'esp' && (
          <div className='realtime-insights-tab-content'>
            <RealTimeWidget
              allRows={espData || []}
              isLoading={realtimeState.espLoading}
              columnObjects={realtimeState.espColumns}
              fixedColumnObjects={[
                {
                  order: 1,
                  label: 'Well name',
                  key: 'wellName',
                },
                {
                  order: 2,
                  label: 'Status',
                  key: 'status',
                },
              ]}
              onExportXLS={handleExport}
            />
          </div>
        )}

        {/* Gas Lift Well Status Tab */}
        {activeTab === 'gl' && (
          <div className='realtime-insights-tab-content'>
            <RealTimeWidget
              isLoading={realtimeState.gasLiftLoading}
              allRows={gasLiftData || []}
              columnObjects={realtimeState.gasLiftColumns}
              fixedColumnObjects={[
                {
                  order: 1,
                  label: 'Well name',
                  key: 'wellName',
                },
                {
                  order: 2,
                  label: 'Status',
                  key: 'status',
                },
              ]}
              onExportXLS={handleExport}
            />
          </div>
        )}

        {/* Rod Lift Well Status Tab */}
        {activeTab === 'rl' && (
          <div className='realtime-insights-tab-content'>
            <RealTimeWidget
              isLoading={realtimeState.rodLiftLoading}
              allRows={rodLiftData || []}
              columnObjects={realtimeState.rodLiftColumns}
              fixedColumnObjects={[
                {
                  order: 1,
                  label: 'Well name',
                  key: 'wellName',
                },
                {
                  order: 2,
                  label: 'Status',
                  key: 'status',
                },
              ]}
              onExportXLS={handleExport}
            />
          </div>
        )}
      </div>
    </div>
  );
};
